import { t } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import { SizedPlot } from 'features/timeSeries/experiment/SizedPlot'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import { TagPicker } from 'features/timeSeries/experiment/TagPicker'
import {
  ExperimentConfig,
  NormalizeCoefficients,
  NormalizeResponse,
  NormMethod,
} from 'features/timeSeries/experiment/types'
import { Layout, PlotData } from 'plotly.js'
import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

const FG = CantileverTheme.colors.grayscale['100']
const GRID = CantileverTheme.colors.grayscale['600']
const COMBINED_COLOR = '#22d3ee'
const VARIANT_COLORS = ['#f97316', '#a78bfa', '#34d399', '#f472b6', '#fbbf24']

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`

const BigNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.blue['400']};
`

const Meta = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`

const Chip = styled.div<{ $accent: string }>`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 7.5rem;
  padding: 0.4rem 0.55rem;
  border-left: 3px solid ${({ $accent }) => $accent};
  background: ${({ theme }) => theme.forecast.pageBackground1};
  font-size: 0.72rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ChipTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.74rem;
`

const ChipValue = styled.div`
  font-variant-numeric: tabular-nums;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const QuickChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.blue['400'] : theme.forecast.border};
  background: ${({ theme, $active }) =>
    $active ? theme.forecast.background2 : theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.72rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
`

const Select = styled.select`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
  ${mobileInputTextStyles}
`

const Error = styled.div`
  color: #ef4444;
  font-size: 0.78rem;
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

/** Human summary of fit coefficients, tolerant of every method's shape. */
const coefficientSummary = (c: NormalizeCoefficients): string => {
  const perTag = (map: Record<string, number>) =>
    Object.entries(map)
      .map(([k, v]) => `${short(k)}=${v.toFixed(4)}`)
      .join(', ')
  const parts: string[] = []
  if (c.intercept !== undefined)
    parts.push(t`intercept=${{ value122_30: c.intercept.toFixed(4) }}`)
  if (c.scale !== undefined)
    parts.push(t`scale=${{ value123_51: c.scale.toFixed(4) }}`)
  if (c.slopes) parts.push(`slopes: ${perTag(c.slopes)}`)
  if (c.quadratic) parts.push(t`x²: ${{ value125_39: perTag(c.quadratic) }}`)
  if (c.exponents) parts.push(`exponents: ${perTag(c.exponents)}`)
  if (c.n_valid !== undefined)
    parts.push(t`n_valid=${{ value127_55: c.n_valid }}`)
  return parts.join('; ')
}

const spearmanText = (value: number | null | undefined): string | null =>
  value === null || value === undefined ? null : `ρ ${value.toFixed(2)}`

export interface NormalizationViewerProps {
  siteId: string
  config: ExperimentConfig
  result: NormalizeResponse | null
  loading: boolean
  error: string | null
  onRun: (against: string[], method: NormMethod) => void
}

/**
 * Normalize-the-target card (Data Studio section of the Experiment tab).
 * The target-vs-normalized comparison stays a Plotly card: it plots derived
 * series (OLS residual / ratio) that don't exist as pens, so the main uPlot
 * chart is not the right surface for it.
 */
export const NormalizationViewer: FC<NormalizationViewerProps> = ({
  siteId,
  config,
  result,
  loading,
  error,
  onRun,
}) => {
  const { t } = useLingui()

  const [against, setAgainst] = useState<string[]>(config.drivers.slice(0, 2))
  const [method, setMethod] = useState<NormMethod>('linear')

  const toggleAgainst = (tag: string) => {
    setAgainst((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const chart = useMemo(() => {
    if (!result?.series) return null
    const { ts, actual, normalized } = result.series
    const data: Partial<PlotData>[] = [
      {
        x: ts,
        y: actual,
        type: 'scatter',
        mode: 'lines',
        name: 'target',
        line: { width: 1.1, color: FG },
      },
      {
        x: ts,
        y: normalized,
        type: 'scatter',
        mode: 'lines',
        name: method === 'ratio' ? 'combined (vs first)' : 'combined residual',
        line: { width: 1.4, color: COMBINED_COLOR },
        yaxis: 'y2',
      },
    ]
    for (const [i, variant] of (result.by_against || []).entries()) {
      if (variant.error || !variant.series?.normalized) continue
      data.push({
        x: variant.series.ts?.length ? variant.series.ts : ts,
        y: variant.series.normalized,
        type: 'scatter',
        mode: 'lines',
        name: short(variant.against),
        line: {
          width: 1.1,
          color: VARIANT_COLORS[i % VARIANT_COLORS.length],
        },
        yaxis: 'y2',
      })
    }
    const layout: Partial<Layout> = {
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      margin: { l: 48, r: 48, t: 28, b: 28 },
      legend: { font: { color: FG, size: 10 } },
      title: {
        text: t`Target vs normalized (combined + per driver)`,
        font: { color: FG, size: 12 },
        x: 0,
      },
      xaxis: {
        type: 'date',
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
      yaxis: {
        title: { text: 'target', font: { color: FG, size: 10 } },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
      yaxis2: {
        title: {
          text: 'normalized',
          font: { color: COMBINED_COLOR, size: 10 },
        },
        tickfont: { color: COMBINED_COLOR, size: 10 },
        overlaying: 'y',
        side: 'right',
      },
    }
    return { data, layout }
  }, [result, method, t])

  return (
    <Wrap>
      <Meta>
        <Trans>
          Normalize the target against selected tags. Flatness = variance
          reduction (1 − var(normalized)/var(target)). Higher = more of the
          target&apos;s movement is explained by the selected tags. With N
          drivers, the chart shows the combined result plus one trace per
          driver.
        </Trans>
      </Meta>
      {!config.target && (
        <Meta>
          <Trans>Set a target first.</Trans>
        </Meta>
      )}
      {config.drivers.length > 0 && (
        <div>
          <Meta>
            <Trans>Quick pick — the experiment&apos;s drivers:</Trans>
          </Meta>
          <ChipRow style={{ marginTop: '0.25rem' }}>
            {config.drivers.map((tag) => {
              const active = against.includes(tag)
              return (
                <QuickChip
                  key={tag}
                  type='button'
                  $active={active}
                  onClick={() => toggleAgainst(tag)}>
                  <TagLabel tagId={tag} />
                  <span>{active ? '✓' : '+'}</span>
                </QuickChip>
              )
            })}
          </ChipRow>
        </div>
      )}
      <TagPicker
        siteId={siteId}
        label={
          <KnobHelp
            label={t`Normalize against`}
            help={KNOB_HELP.normalizeAgainst}
          />
        }
        value={against}
        onChange={setAgainst}
        multi
        exclude={config.target ? [config.target] : []}
      />
      <Row>
        <KnobHelp label={t`Method`} help={KNOB_HELP.normalizeMethod} />
        <Select
          value={method}
          onChange={(e) => setMethod(e.target.value as NormMethod)}>
          <option value='linear'>Linear regression residual</option>
          <option value='ratio'>Ratio (combined = first tag)</option>
          <option value='quadratic'>Quadratic (x + x²) residual</option>
          <option value='log'>Log (ln x) residual — needs x &gt; 0</option>
          <option value='power'>Power (log-log) — needs x, y &gt; 0</option>
        </Select>
        <Button
          disabled={!config.target || !against.length || loading}
          onClick={() => onRun(against, method)}>
          {loading ? t`Computing…` : t`Normalize`}
        </Button>
      </Row>
      {error && <Error>{error}</Error>}
      {result && !result.ok && (
        <Error>{result.error || t`normalize failed`}</Error>
      )}
      {result?.ok && (
        <>
          <div>
            <BigNumber>{result.variance_reduction_pct.toFixed(1)}%</BigNumber>
            <Meta>
              <Trans>combined variance reduction · flatness=</Trans>
              {result.flatness.toFixed(4)}
              <Trans> · n=</Trans>
              {result.n_points}
              <Trans> · std</Trans> {result.std_before.toFixed(3)} →{' '}
              {result.std_after.toFixed(3)}
            </Meta>
            {result.coefficients && (
              <Meta>{coefficientSummary(result.coefficients)}</Meta>
            )}
          </div>
          <ChipRow>
            <Chip $accent={COMBINED_COLOR}>
              <ChipTitle>combined</ChipTitle>
              <ChipValue>{result.variance_reduction_pct.toFixed(1)}%</ChipValue>
              <span>flatness {result.flatness.toFixed(4)}</span>
            </Chip>
            {(result.by_against || []).map((variant, i) => {
              const spearman = spearmanText(
                variant.spearman ?? result.spearman?.[variant.against]
              )
              return (
                <Chip
                  key={variant.against}
                  $accent={VARIANT_COLORS[i % VARIANT_COLORS.length]}>
                  <ChipTitle>
                    <TagLabel tagId={variant.against} />
                  </ChipTitle>
                  {variant.error ? (
                    <span>
                      {variant.error}
                      {spearman ? ` · ${spearman}` : ''}
                    </span>
                  ) : (
                    <>
                      <ChipValue>
                        {(variant.variance_reduction_pct ?? 0).toFixed(1)}%
                      </ChipValue>
                      <span>
                        flatness {(variant.flatness ?? 0).toFixed(4)}
                        {variant.n_points !== null
                          ? t` · n=${{ value356_38: variant.n_points }}`
                          : ''}
                        {spearman ? ` · ${spearman}` : ''}
                      </span>
                    </>
                  )}
                </Chip>
              )
            })}
          </ChipRow>
          {chart && (
            <SizedPlot
              $heightRem={14}
              data={chart.data}
              layout={chart.layout}
            />
          )}
        </>
      )}
    </Wrap>
  )
}
